import { useCallback, useEffect, useMemo, useRef } from 'react';

import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import { createNativeEmbedChildClient } from '@atlassian/native-embeds-core/services/client/child/main';
import { maybeParseNativeEmbedParams } from '@atlassian/native-embeds-core/services/client/iframe-integration/main';
import {
	NativeEmbedCoreFeature,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types/features/main';
import type { NativeEmbedChildCommand } from '@atlassian/native-embeds-core/types/commands/types';

import { UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import {
	acquireSharedNativeEmbedChildClient,
	type NativeEmbedChildClient,
	type ReadPageContent as NativeEmbedReadPageContent,
	type ReadPageContentSource as NativeEmbedReadPageContentSource,
	registerReadPageContent,
} from './nativeEmbedChildClient.ts';

export type ReadPageContent = NativeEmbedReadPageContent;
export type ReadPageContentSource = NativeEmbedReadPageContentSource;

/**
 * Acquires the native embed child client when a page is running inside a
 * native embed iframe (i.e. the URL contains `nativeEmbedChildId` and
 * `nativeEmbedParentId` query params set by the host). Gated behind the
 * `cc-maui-experiment` experiment.
 *
 * Should be called from embedded view pages, embedded edit pages, and
 * embedded live docs.
 *
 * Multiple components may call this hook for the same iframe; see `nativeEmbedChildClient.ts` for
 * how the client is shared between them.
 *
 * @returns sendChildCommand — a stable callback to send a child command to the
 * native embed host, or a no-op if not running inside a native embed iframe.
 * @returns setReadPageContent — a stable callback registering a page-content provider for
 * read-content commands, which returns an unregister function for the caller's cleanup. Both are
 * no-ops if not running inside a native embed iframe.
 */
export const usePageNativeEmbedClient = (): {
	sendChildCommand: (command: NativeEmbedChildCommand) => void;
	triggerToggleFullScreen: () => void;
	setReadPageContent: (readPageContent: ReadPageContent) => () => void;
	isNativeEmbed: boolean;
} => {
	const clientRef = useRef<NativeEmbedChildClient | null>(null);

	const nativeEmbedParams = useMemo(() => {
		const isNativeEmbedEnabled =
			UNSAFE_expValNoExposure('cc-maui-experiment', 'isEnabled', false) ||
			fg('platform_native_embeds_rollout_non_maui_experience');

		if (!isNativeEmbedEnabled || typeof window === 'undefined') {
			return null;
		}

		return maybeParseNativeEmbedParams(new URLSearchParams(window.location.search));
	}, []);
	const isNativeEmbed = !!nativeEmbedParams;

	useEffect(() => {
		if (!nativeEmbedParams) {
			return;
		}

		if (
			fg('maui_remixable_page_native_embeds') ||
			fg('confluence_native_embed_shared_child_client')
		) {
			const { client, release } = acquireSharedNativeEmbedChildClient(nativeEmbedParams);
			clientRef.current = client;

			return () => {
				clientRef.current = null;
				release();
			};
		}

		const bridge = getBrowserBridgeAPI();
		const { childClientId, parentClientId } = nativeEmbedParams;

		const supportedFeatures = new Set<NativeEmbedFeature>([NativeEmbedCoreFeature.NotifyHostClick]);

		const client = createNativeEmbedChildClient(
			bridge,
			childClientId,
			parentClientId,
			supportedFeatures,
		);

		clientRef.current = client;

		return () => {
			client.destroy();
			clientRef.current = null;
		};
	}, [nativeEmbedParams]);

	const sendChildCommand = useCallback((command: NativeEmbedChildCommand) => {
		clientRef.current?.sendChildCommand(command);
	}, []);

	const triggerToggleFullScreen = useCallback(() => {
		clientRef.current?.sendToggleFullScreen();
	}, []);

	const setReadPageContent = useCallback(
		(readPageContent: ReadPageContent) => {
			if (!fg('maui_remixable_page_native_embeds')) {
				return () => {};
			}
			if (!isNativeEmbed) {
				return () => {};
			}

			return registerReadPageContent(readPageContent);
		},
		[isNativeEmbed],
	);

	return { sendChildCommand, triggerToggleFullScreen, setReadPageContent, isNativeEmbed };
};
